<template>
  <form @submit.prevent="handleGoalSubmission">
    <div
      :class="[
        'columns',
        'is-mobile',
        'goal__wrapper',
        'has-text-dark',
        { 'goal__wrapper--hover': isHoveringGoal || isEditingGoal },
      ]"
      @mouseover="handleHoverGoal"
      @focusin="handleEnableGoalEditing()"
      @mouseleave="handleHoverGoal"
      @focusout="handleCompleteGoalEditing()"
    >
      <div
        :class="[
          'column goal__text-area',
          size === 'medium' ? 'is-size-5' : '',
          size === 'small' ? 'is-size-6' : '',
        ]"
        ref="goalTextArea"
        v-contenteditable:newGoalText="true"
        :placeholder="goalPlaceholderText"
        @keydown.enter="handleGoalSubmission"
      />
      <div class="column is-1">
        <b-icon
          v-if="isHoveringGoal && !isEditingGoal"
          icon="pencil-box-outline"
          type="is-grey-light"
          @click.native="handleClickEditIcon"
          @keyup.enter="handleClickEditIcon"
        />
      </div>
    </div>
    <div class="update-goal-button__container">
      <b-button
        v-if="isEditingGoal || isGoalUpdating"
        native-type="submit"
        type="is-link"
        :size="`is-${size}`"
        :disabled="newGoalText === ''"
        :loading="isGoalUpdating"
      >
        Save
      </b-button>
    </div>
  </form>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import * as Types from '@/constants/Types';
import * as GoalActions from '@/store/actions/Goal.actions';

export default {
  name: 'GoalTextInput',

  props: {
    activeGoalId: {
      type: String,
      required: false,
    },
    activeGoalDate: {
      type: String,
      required: true,
    },
    activeUserId: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      required: false,
      validator(propValue) {
        // The value must match one of these strings
        return ['small', 'medium'].indexOf(propValue) !== -1;
      },
      default: 'medium',
    },
  },

  data() {
    return {
      isHoveringGoal: false,
      goalPlaceholderText: 'What\'s the most important thing you need to do today?',
      isGoalUpdating: false,
      isEditingGoal: false,
      newGoalText: '',
    };
  },

  computed: {
    ...mapState({
      goals: state => state.goal.goals,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    goalText: {
      get() {
        return this.activeGoal.goalText;
      },
      set(newValue) {
        this.newGoalText = newValue;
      },
    },
    payloadForNewGoal() {
      return {
        goalDate: this.activeGoalDate,
        goalText: this.newGoalText,
        goalStatus: Types.GOAL_UNMARKED,
        workspaceId: this.activeWorkspaceId,
        userId: this.activeUserId,
      };
    },
    payloadForUpdatedGoal() {
      return {
        goalText: this.newGoalText,
      };
    },
    activeGoal() {
      if (this.goals[this.activeGoalId]) {
        return { ...this.goals[this.activeGoalId] };
      }

      return {
        goalText: '',
        goalDate: this.activeGoalDate,
        goalStatus: Types.GOAL_UNMARKED,
      };
    },
  },

  created() {
    this.newGoalText = this.activeGoal.goalText;
  },

  watch: {
    goalText(newValue) {
      this.newGoalText = newValue;
    },
    activeGoalId() {
      this.newGoalText = this.activeGoal.goalText;
    },
  },

  methods: {
    ...mapActions({
      createNewGoal: GoalActions.CREATE_NEW_GOAL,
      updateGoal: GoalActions.UPDATE_GOAL,
    }),
    handleHoverGoal($event) {
      // Two alternatives, mouseover, or mouseout, we only check for the former.
      const isHoveringGoal = $event.type === Types.EVENT_MOUSEOVER || false;

      // Don't show edit button or hover effect if user is editing their goal.
      // Still remove hover state in case of mouseout event.
      if (!this.isEditingGoal || !isHoveringGoal) {
        this.isHoveringGoal = isHoveringGoal;
      }
    },
    handleEnableGoalEditing() {
      this.isEditingGoal = true;
    },
    handleCompleteGoalEditing() {
      if (this.newGoalText !== '') {
        this.handleGoalSubmission();
      }

      this.isEditingGoal = false;
    },
    handleClickEditIcon() {
      const goalTextAreaElement = this.$refs.goalTextArea;
      goalTextAreaElement.focus();

      // @TODO -- This code attempts to set the cursor position when clicking the
      // edit pencil icon. IT DOES NOT WORK! Needs to be fixed.
      const range = document.createRange();
      const sel = window.getSelection();
      range.setStart(goalTextAreaElement.childNodes[0], 5);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    async handleGoalSubmission($event) {
      if ($event) $event.preventDefault();
      this.isGoalUpdating = true;

      if (!this.activeGoalId) {
        await this.createNewGoal(this.payloadForNewGoal);
      } else {
        await this.updateGoal({ goalId: this.activeGoalId, updatedGoal: this.payloadForUpdatedGoal });
      }

      this.isGoalUpdating = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.goal__wrapper {
  cursor: pointer;
  min-height: 50px;

  &--hover {
    background-color: $white-bis;
  }
}

.goal__text-area {
  &:focus {
    outline: none;
  }
}

[contenteditable=true]:empty:before {
  content: attr(placeholder);
  display: block; /* For Firefox */
  color: $grey-light;
}

.update-goal-button__container {
  margin-top: $global-whitespace-small;
  display: flex;
  justify-content: flex-end;
}
</style>
